import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { FolderOpen, FileText, FileType2, Trash2, Share2, CheckCircle2, Search, Plus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const KINDS = {
  experience: { base: '/api/experience-certificates', title: 'Experience Certificate', badge: 'bg-sky-100 text-sky-700', issue: '/experience-certificate', docx: false, refKey: 'reference_number' },
  salary: { base: '/api/salary-certificates', title: 'Salary Certificate', badge: 'bg-emerald-100 text-emerald-700', issue: '/salary-certificate', docx: false, refKey: 'reference_number' },
  noc: { base: '/api/noc-certificates', title: 'No Objection Certificate', badge: 'bg-teal-100 text-teal-700', issue: '/noc-certificate', docx: true, refKey: 'serial_number' },
};

const fmt = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };

const DocumentRepository = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [kindFilter, setKindFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [exp, sal, noc] = await Promise.all([
        axios.get(`${API}/api/experience-certificates`).then(r => r.data).catch(() => []),
        axios.get(`${API}/api/salary-certificates`).then(r => r.data).catch(() => []),
        axios.get(`${API}/api/noc-certificates`).then(r => r.data).catch(() => []),
      ]);
      const norm = (arr, kind) => (arr || []).map(x => ({
        ...x, _kind: kind,
        _ref: x[KINDS[kind].refKey] || '',
        _title: kind === 'noc' && x.purpose_type ? `NOC · ${x.purpose_type === 'travel' ? (x.destination_country || 'Travel') : 'Work'}` : KINDS[kind].title,
      }));
      const all = [...norm(exp, 'experience'), ...norm(sal, 'salary'), ...norm(noc, 'noc')];
      all.sort((a, b) => (b.issue_date || b.created_at || '').localeCompare(a.issue_date || a.created_at || ''));
      setRows(all);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter(r => {
    if (kindFilter !== 'all' && r._kind !== kindFilter) return false;
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (r.employee_name || '').toLowerCase().includes(s) || (r._ref || '').toLowerCase().includes(s);
  }), [rows, q, kindFilter]);

  const share = async (r) => {
    try { await axios.post(`${API}${KINDS[r._kind].base}/${r.id}/share`); toast({ title: 'Sent to employee' }); load(); }
    catch (e) { toast({ title: 'Could not share', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
  };
  const del = async (r) => {
    if (!window.confirm('Delete this document?')) return;
    try { await axios.delete(`${API}${KINDS[r._kind].base}/${r.id}`); toast({ title: 'Deleted' }); load(); }
    catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const counts = useMemo(() => ({
    all: rows.length,
    experience: rows.filter(r => r._kind === 'experience').length,
    salary: rows.filter(r => r._kind === 'salary').length,
    noc: rows.filter(r => r._kind === 'noc').length,
  }), [rows]);

  return (
    <div className="space-y-4" data-testid="document-repository-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FolderOpen className="h-6 w-6 text-indigo-600" /> Document Repository</h1>
          <p className="text-gray-600 mt-1">All issued Experience, Salary &amp; NOC certificates in one searchable place.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/experience-certificate')} data-testid="repo-issue-experience"><Plus className="h-4 w-4 mr-1" /> Experience</Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/salary-certificate')} data-testid="repo-issue-salary"><Plus className="h-4 w-4 mr-1" /> Salary</Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/noc-certificate')} data-testid="repo-issue-noc"><Plus className="h-4 w-4 mr-1" /> NOC</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input placeholder="Search by employee name or reference / serial…" value={q} onChange={e => setQ(e.target.value)} className="pl-9 h-9" data-testid="repo-search" />
            </div>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="h-9 w-48" data-testid="repo-kind-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All documents ({counts.all})</SelectItem>
                <SelectItem value="experience">Experience ({counts.experience})</SelectItem>
                <SelectItem value="salary">Salary ({counts.salary})</SelectItem>
                <SelectItem value="noc">NOC ({counts.noc})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-gray-400 py-10">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-10">No documents found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-gray-500 border-b">
                  <tr>
                    <th className="py-2 pr-2">Employee</th>
                    <th className="py-2 pr-2">Type</th>
                    <th className="py-2 pr-2">Reference</th>
                    <th className="py-2 pr-2">Issued</th>
                    <th className="py-2 pr-2">Shared</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={`${r._kind}-${r.id}`} className="border-b last:border-0" data-testid={`repo-row-${r.id}`}>
                      <td className="py-2 pr-2 font-medium text-gray-800">{r.employee_name}</td>
                      <td className="py-2 pr-2"><Badge className={`${KINDS[r._kind].badge} text-[11px]`}>{r._title}</Badge></td>
                      <td className="py-2 pr-2 font-mono text-[11px] text-gray-500">{r._ref}</td>
                      <td className="py-2 pr-2 text-gray-600 whitespace-nowrap">{fmt(r.issue_date)}</td>
                      <td className="py-2 pr-2">{r.shared_with_employee ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-0.5" />Yes</Badge> : <span className="text-gray-300 text-xs">—</span>}</td>
                      <td className="py-2">
                        <div className="flex gap-1 justify-end">
                          <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => window.open(`${API}${KINDS[r._kind].base}/${r.id}/pdf`, '_blank')} title="PDF" data-testid={`repo-pdf-${r.id}`}><FileText className="h-4 w-4 text-rose-600" /></Button>
                          {KINDS[r._kind].docx && (
                            <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => window.open(`${API}${KINDS[r._kind].base}/${r.id}/docx`, '_blank')} title="Word" data-testid={`repo-docx-${r.id}`}><FileType2 className="h-4 w-4 text-blue-600" /></Button>
                          )}
                          {r.employee_id && !r.shared_with_employee && (
                            <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => share(r)} title="Send to employee" data-testid={`repo-share-${r.id}`}><Share2 className="h-4 w-4 text-teal-600" /></Button>
                          )}
                          <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => del(r)} title="Delete" data-testid={`repo-delete-${r.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentRepository;
